import React, { Component } from 'react';
import PropTypes from 'prop-types';

import leaflet from 'leaflet';

import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import ViewStack from '../map/ViewStack';
import ChipControl from '../map/ChipControl';
import ZoomControl from '../map/ZoomControl';
import ScaleControl from '../map/ScaleControl';
import LatLngControl from '../map/LatLngControl';
import BaseLayerControl from '../map/BaseLayerControl';
import LayerControl from '../map/LayerControl';
import RegionControl from '../map/RegionControl';
import DateControl from '../map/DateControl';
import GraphControl from '../map/GraphControl';
import GraphLegendControl from '../map/GraphLegendControl';
import GridControl from '../map/GridControl';
import GridOverlay from '../map/GridOverlay';
import InfoPanel from '../map/InfoPanel';
import GraphPanel from '../map/GraphPanel';
import Layer from '../map/Layer';
import Boundary from '../map/Boundary';
import Marker from '../map/Marker';

const styles = (theme) => {
    return {
        background: {
            position: 'absolute',
            height: '100%',
            width: '100%',
            zIndex: -1,
            backgroundColor: theme.palette.common.black,
        },
        container: {
            position: 'absolute',
            height: '100%',
            width: '100%',
            zIndex: -1,
            textAlign: 'left',
            backgroundColor: theme.palette.common.black,
        },
        progress: {
            outline: 'none',
            position: 'absolute',
            width: 60,
            height: 60,
            top: 'calc(50% - 30px)',
            left: 'calc(50% - 30px)',
            textAlign: 'center',
            backgroundColor: 'transparent',
            margin: 'auto',
            color: theme.palette.primary.light,
        },
        progressOverlay: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            opacity: 0.8,
            backgroundColor: theme.palette.common.black,
            zIndex: 1900,
        },
        chipGroup: {
            position: 'absolute',
            top: 16,
            left: 16,
            width: 480,
            height: 32,
            zIndex: 900,
        },
        zoomControl: {
            position: 'absolute',
            bottom: 20,
            right: 16,
            zIndex: 900,
        },
        scaleControl: {
            position: 'absolute',
            bottom: 52,
            right: 64,
            zIndex: 500,
        },
        latLngControl: {
            position: 'absolute',
            bottom: 20,
            right: 64,
            zIndex: 500,
        },
        layerControl: {
            position: 'absolute',
            bottom: 90,
            right: 16,
            zIndex: 900,
        },
        graphControl: {
            position: 'absolute',
            bottom: 128,
            right: 16,
            zIndex: 900,
        },
        gridControl: {
            position: 'absolute',
            bottom: 166,
            right: 16,
            zIndex: 900,
        },
        baseLayerControl: {
            position: 'absolute',
            bottom: 204,
            right: 16,
            zIndex: 900,
        },
        graphLegendControl: {
            position: 'absolute',
            bottom: 'calc(50% - 25px)',
            right: 16,
            zIndex: 500,
        },
        regionControl: {
            position: 'absolute',
            top: 16,
            right: 16,
            width: 110,
            height: 28,
            zIndex: 900,
        },
        dateControl: {
            position: 'absolute',
            top: 16,
            right: 16,
            width: 130,
            height: 28,
            zIndex: 900,
        },
        gridOverlay: {
            position: 'absolute',
            zIndex: 100,
        },
        infoPanel: {
            position: 'absolute',
            bottom: 20,
            left: 16,
            zIndex: 600,
        },
        graphPanel: {
            position: 'absolute',
            padding: 0,
            bottom: 20,
            left: 16,
            right: 64,
            height: '45%',
            minHeight: 300,
            zIndex: 600,
        },
    };
}

class Viewer extends Component {
    constructor(props) {
        super(props);

        const b = [[-10, 114], [14, 93]];

        const defaultVal = {
            uid: 'default',
            name: 'default',
            key: 'data',
            region: {
                bounds: leaflet.latLngBounds(b),
            },
        };

        const defaultLayer = {
            base: {
                name: 'Base',
                data: [{
                    title: '',
                }],
                selectedLayerIndex: 0,
            }
        };

        var viewStack = new ViewStack(defaultVal, this._updateViewStack);
        viewStack.peek().layer = defaultLayer;
        viewStack.peek().selectedLayerType = 'base';

        this.state = {
            viewStack: viewStack,
            map: null,
            graphPanelOpen: false,
            gridOverlayShow: false,
            loaded: false,
            prevIndex: null,
            prevType: null,
            prevLayer: null,
        };

        document.title = 'Viewer \u00b7 Alpha2';
    }

    componentDidMount = () => {
        this._isMount = true;

        const m = leaflet.map('mapContainer', {
            zoom: 4,
            center: this.state.viewStack.peek().region.bounds.getCenter(),
            minZoom: this.state.viewStack.peek().zoom.min,
            worldCopyJump: true,
            zoomControl: false,
            maxBoundsViscosity: 1.0,
            attributionControl: false,
            preferCanvas: true,
        });

        this.state.viewStack.setMap(m);

        const { theme } = this.props;

        this._boundary = new Boundary(
            this.state.viewStack, m, this.props.getAccessCode, this._processError);

        this._boundary.generateStyles(theme);

        this._layer = new Layer(
            this.state.viewStack, m, this.props.getAccessCode, this._processError);

        this._marker = new Marker(
            this.state.viewStack, m, this._boundary, this.props.getAccessCode, this._processError);

        this.setState({
            map: m,
        });

        this._firstLoad();
    }

    componentWillUnmount = () => {
        this._isMount = false;
        this._boundary.unmount();
        this._layer.unmount();
        this.state.viewStack.popFrom(0);
        this.state.map.off();
    }

    render = () => {
        const { classes } = this.props;

        return (
            <div className={classes.background}>
                <div
                    className={classes.container}
                    id='mapContainer'
                />
                {this._renderComponents()}
                {this._renderLoadingProgress()}
            </div>
        );
    }

    _renderLoadingProgress = () => {
        if (this.state.loaded) {
            return null;
        }

        const { classes } = this.props;

        return (
            <div className={classes.progressOverlay}>
                <CircularProgress
                    className={classes.progress}
                    size={60}
                />
            </div>
        )
    }

    _renderComponents = () => {
        if (!this.state.map) {
            return null;
        }

        const { classes } = this.props;

        return (
            <div>
                <ChipControl
                    className={classes.chipGroup}
                    viewStack={this.state.viewStack}
                    map={this.state.map}
                    onDelete={this._handleDeleteChip}
                />
                <ZoomControl
                    className={classes.zoomControl}
                    map={this.state.map}
                />
                <ScaleControl
                    className={classes.scaleControl}
                    map={this.state.map}
                    gridMode={this.state.gridOverlayShow}
                />
                <LatLngControl
                    className={classes.latLngControl}
                    map={this.state.map}
                />
                <LayerControl
                    className={classes.layerControl}
                    viewStack={this.state.viewStack}
                    map={this.state.map}
                    onChange={this._handleLayerChange}
                />
                <RegionControl
                    className={classes.regionControl}
                    getAccessCode={this.props.getAccessCode}
                    viewStack={this.state.viewStack}
                    map={this.state.map}
                />
                <DateControl
                    className={classes.dateControl}
                    viewStack={this.state.viewStack}
                    onChange={this._handleLayerIndexChange}
                />
                <GraphControl
                    className={classes.graphControl}
                    viewStack={this.state.viewStack}
                    onClick={this._handleGraphControl}
                />
                <GraphLegendControl
                    className={classes.graphLegendControl}
                    getAccessCode={this.props.getAccessCode}
                    viewStack={this.state.viewStack}
                />
                <GridControl
                    className={classes.gridControl}
                    viewStack={this.state.viewStack}
                    map={this.state.map}
                    onClick={this._handleGridControl}
                />
                <BaseLayerControl
                    className={classes.baseLayerControl}
                    viewStack={this.state.viewStack}
                    onClick={this._handleBaseLayerControl}
                />
                <GridOverlay
                    className={classes.gridOverlay}
                    show={this.state.gridOverlayShow}
                    map={this.state.map}
                />
                <InfoPanel
                    className={classes.infoPanel}
                    getAccessCode={this.props.getAccessCode}
                    loaded={this.state.loaded}
                    viewStack={this.state.viewStack}
                />
                <GraphPanel
                    className={classes.graphPanel}
                    getAccessCode={this.props.getAccessCode}
                    viewStack={this.state.viewStack}
                    open={this.state.graphPanelOpen}
                />
            </div>
        );
    }

    _firstLoad = async () => {
        // Initialization for layer
        const viewStack = this.state.viewStack;
        const view = viewStack.peek();
        const type = view.selectedLayerType;
        const index = 0;

        this._layer.update();
        await this._boundary.update();

        if (this._jumpToRegion()) {
            return;
        }

        this.setState({
            loaded: true,
            prevIndex: index,
            prevType: type,
            prevLayer: this._layer,
        });
    }

    _handleDeleteChip = async () => {
    }

    _handleLayerChange = async (type) => {
        await this._layer.activate(type, null, null, false);

        this.setState({
            loaded: true,
            prevType: type,
            prevLayer: this._layer,
        });
    }

    _handleShutdownPrevLayer = async () => {
        if (this.state.prevIndex !== null && this.state.prevType !== null &&
            this.state.prevView !== null && this.state.prevLayer !== null) {
            await this.state.prevLayer.activate(this.state.prevType, this.state.prevIndex, false, false);

        }
    }

    _handleLayerIndexChange = async (index) => {

        const viewStack = this.state.viewStack;
        const view = viewStack.peek();

        const type = view.selectedLayerType;
        view.layer[type].data[index].baseLayerShow = true;
        const baseLayerShow = view.layer[type].data[index].baseLayerShow;

        //Shutting down previous layer
        if (this.state.prevIndex !== null && this.state.prevType !== null &&
            this.state.prevView !== null && this.state.prevLayer !== null) {
            await this.state.prevLayer.activate(this.state.prevType, this.state.prevIndex, false, false);
        }

        await this._layer.activate(type, index, baseLayerShow, false);

        this.setState({
            loaded: true,
            prevIndex: index,
            prevType: type,
            prevLayer: this._layer,
        });
    }

    _handleBaseLayerControl = async (state) => {
        const viewStack = this.state.viewStack;
        const view = viewStack.peek();
        const type = view.selectedLayerType;
        const index = view.layer[type].selectedLayerIndex;

        if (view.layer[type].data[index].baseLayerShow !== state) {
            await this._layer.activate(type, index, state, false);
        }
    }

    _handleGraphControl = (state) => {
        if (this.state.graphPanelOpen !== state) {
            this.setState({
                graphPanelOpen: state,
            });
        }
    }

    _handleGridControl = (state) => {
        if (this.state.gridOverlayShow !== state) {
            this.setState({
                gridOverlayShow: state,
            });
        }
    }

    _updateViewStack = async (oldStack) => {
        if (!this._isMount) {
            return;
        }

        this._boundary.update();
        this._marker.update();
        this._layer.update(oldStack).then(() => {
            this.setState({
                loaded: true,
            });
        });

        const viewStack = this.state.viewStack;
        const view = viewStack.peek(-2);

        if (this.props.setSharedProps) {
            this.props.setSharedProps({
                region: (view) ? view.idKey : '',
            });
        }

        this.setState({
            loaded: true,
        });
    }

    _jumpToRegion = () => {
        const viewStack = this.state.viewStack;
        const view = viewStack.peek();
        const index = viewStack.length() - 1;
        const regionKey = [this.props.sharedProps.region];
        const targetKey = regionKey[index];

        var result = false;

        if (targetKey) {
            view.region.boundsObj.eachLayer((l) => {
                const compareResult = targetKey.localeCompare(
                    l.feature.properties.key, 'en', { sensitivity: 'base' });

                if (compareResult === 0) {
                    const bounds = l.getBounds();
                    const latLngBounds = leaflet.latLngBounds(bounds);
                    viewStack.pushLayerObj(l, bounds, latLngBounds);
                    result = true;
                }
            });
        }

        this._regionKey = [];

        return result;
    }

    _processError = (error) => {
        if (error.response && error.response.status === 404) {
            return;
        }

        if (this.props.onError) {
            this.props.onError(error);
        }
    }
}

Viewer.propTypes = {
    getAccessCode: PropTypes.func.isRequired,
    setAccessCode: PropTypes.func.isRequired,
    sharedProps: PropTypes.object.isRequired,
    setSharedProps: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles, { withTheme: true })(Viewer);
